<template>
  <div class="organization-overview  px-5">

    <!-- Licence gate: collection only runs for a vehicle carrying the Connected Vehicle Meter licence. -->
    <v-alert
        v-if="isConnectedVehicle && meterDevice && meterDevice.vehicle && meterDevice.vehicle.id && vehicleLicenceOk === false"
        type="warning"
        text
        dense
        class="mb-4"
    >
      {{ $t('meter-device.connected-vehicle.licence-missing') }}
    </v-alert>

    <div class="grid pa-0">

      <headline>
        {{ $tc('meter-device.meter-device') }}
      </headline>
      <v-row>
        <v-col class="label" sm="3">
          {{ $t('meter-device.type') }} :
        </v-col>
        <v-col sm="9">
          {{ $t(`enums.meter-device.${meterDevice.type}`) }}
          <v-btn class="ml-5" small :to="{name: 'meter-device.edit'}">
            <v-icon left>mdi-cog</v-icon>
            {{ $t('edit')}}
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="label"  sm="3">
          {{ $t('meter-device.statement-mode') }} :
        </v-col>
        <v-col sm="3">
          <!-- A connected-vehicle meter runs on statementMode=API (telematics), which the generic label calls
               "API Domoconso" — misleading here. Show a telematics-specific label for this type. -->
          {{ meterDevice.type === 'connected-vehicle'
              ? $t('meter-device.statement-mode-connected-vehicle')
              : $t('enums.statement-mode.' + meterDevice.statementMode) }}
        </v-col>

        <v-col class="label"  sm="3">
          {{ $t('meter-device.statement-type') }} :
        </v-col>
        <v-col sm="3">
          {{ $t('enums.statement-type.' + meterDevice.statementType) }}
        </v-col>
      </v-row>


      <headline class="mt-10">
        {{ $tc("general-information") }}
      </headline>

      <v-row>
        <v-col class="label" sm="3">
          {{ $t('name') }} :
        </v-col>
        <v-col sm="3">
          {{ meterDevice.name ? meterDevice.name : '-' }}
        </v-col>
        <!-- Connected vehicle: registration plate instead of external reference. -->
        <template v-if="isConnectedVehicle">
          <v-col class="label" sm="3">
            {{ $t('vehicle-registration') }} :
          </v-col>
          <v-col sm="3">
            <router-link
                v-if="meterDevice.vehicle && meterDevice.vehicle.id && meterDevice.vehicle.registrationNumber"
                :to="{ name: 'vehicle.detail', params: { id: meterDevice.vehicle.id } }"
            >
              {{ meterDevice.vehicle.registrationNumber }}
            </router-link>
            <template v-else>{{ meterDevice.vehicle && meterDevice.vehicle.registrationNumber ? meterDevice.vehicle.registrationNumber : '-' }}</template>
          </v-col>
        </template>
        <template v-else>
          <v-col class="label" sm="3">
            {{ $t('external-reference') }} :
          </v-col>
          <v-col sm="3">
            {{ meterDevice.externalId}}
          </v-col>
        </template>
      </v-row>

      <!-- Brand/model come from the connected vehicle for that type. -->
      <v-row  v-if="!isExternalOperator">
        <v-col class="label" sm="3">
          {{ $t('meter-device.brand') }} :
        </v-col>
        <v-col sm="3">
          {{ isConnectedVehicle ? ((meterDevice.vehicle && meterDevice.vehicle.brand) || '-') : (meterDevice.brand || '-') }}
        </v-col>
        <v-col class="label" sm="3">
          {{ $t('meter-device.model') }} :
        </v-col>
        <v-col sm="3">
          {{ isConnectedVehicle ? ((meterDevice.vehicle && meterDevice.vehicle.model) || '-') : (meterDevice.model || '-') }}
        </v-col>
      </v-row>

      <v-row v-if="isConnectedVehicle">
        <v-col class="label" sm="3">
          {{ $t('meter-device.connected-vehicle.geofence-radius') }} :
        </v-col>
        <v-col sm="3">
          {{ meterDevice.geofenceRadiusM != null ? meterDevice.geofenceRadiusM + ' m' : '-' }}
        </v-col>
      </v-row>

      <headline class="mt-10">
        {{ $tc("customer.customer") }}
      </headline>
      <v-row >
        <v-col class="label"  sm="3">
          {{ $tc('customer.customer') }} :
        </v-col>
        <v-col  md="9">
          <router-link :to="{name: 'customer.detail', params: {id: meterDevice.customer.id }}" v-if="meterDevice.customer?.id">
           {{ customerName(meterDevice.customer) }}
          </router-link>
          <template v-else>-</template>
        </v-col>
      </v-row>

      <headline class="mt-10">
        {{ $tc("location.location") }}
      </headline>
      <v-row>
        <v-col class="label"  sm="3">
          {{ $t('name') }} :
        </v-col>
        <v-col sm="3">
          {{ meterDevice.location?.name }}
        </v-col>
        <v-col class="label"  sm="3">
          {{ $t('organization.organization') }} :
        </v-col>
        <v-col sm="3" class="text-center">
          <router-link :to="{name: 'organizations.detail', params: {id: meterDevice.organization.id }}" v-if="meterDevice.organization">
            {{ meterDevice.organization.name}}
          </router-link>
          <template v-else>-</template>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="label"  sm="3">
          {{ $t('street') }} :
        </v-col>
        <v-col sm="3">
          {{ meterDevice.location?.address?.street }}
        </v-col>
        <v-col class="label"  sm="3">
          {{ $t('state') }} :
        </v-col>
        <v-col sm="3">
          {{ meterDevice.location?.address?.state }}
        </v-col>
      </v-row>
      <v-row>
        <v-col class="label"  sm="3">
          {{ $t('city') }} :
        </v-col>
        <v-col sm="3">
          {{ meterDevice.location?.address?.city }}
        </v-col>
        <v-col class="label"  sm="3">
          {{ $t('zipcode') }} :
        </v-col>
        <v-col sm="3">
          {{ meterDevice.location?.address?.zipcode }}
        </v-col>
      </v-row>

      <template v-if="!isExternalOperator">
        <headline class="mt-10">
          {{ $t("settings") }}
        </headline>

        <!-- Meter index values: not for a connected vehicle (session-based, no index). -->
        <template v-if="!isConnectedVehicle">
          <v-row>
            <v-col class="label"  sm="3">
              {{ $t('meter-device.initial-meter-value') }} :
            </v-col>
            <v-col sm="3">
              {{ meterDevice.initialMeterValue ? (meterDevice.initialMeterValue / 1000) + " " + $t('usages.kwh') : '-' }}
            </v-col>

            <v-col class="label"  sm="3">
              {{ $t('meter-device.max-meter') }} :
            </v-col>
            <v-col sm="3">
              {{ meterDevice.maxMeterValue ? (meterDevice.maxMeterValue / 1000) + " " + $t('usages.kwh') : '-' }}
            </v-col>
          </v-row>
          <v-row>
            <v-col class="label"  sm="3">
              {{ $t('meter-device.last-meter-value') }} :
            </v-col>
            <v-col sm="3">
              {{ meterDevice.lastMeterValue ? meterDevice.lastMeterValue / 1000 + " " + $t('usages.kwh') : '-' }}
            </v-col>
            <v-col class="label"  sm="3">
              {{ $t('meter-device.last-meter-date') }} :
            </v-col>
            <v-col sm="3">
               <span v-if="meterDevice.lastMeterDate">
                {{ meterDevice.lastMeterDate |  formatDate($t('format_date'))}}
              </span>
              <span v-else>-</span>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="label"  sm="3">
              {{ $t('electricity-tariff.value-per-kwh') }} :
            </v-col>
            <v-col sm="3">
              <meter-device-energy-price-module
                  :meter-device-id="meterDevice.id"
                  readonly
              />
            </v-col>
          </v-row>
        </template>

        <!-- Connected vehicle: only the last-reading date + €/kWh price, side by side. -->
        <v-row v-else>
          <v-col class="label"  sm="3">
            {{ $t('meter-device.last-meter-date') }} :
          </v-col>
          <v-col sm="3">
            <span v-if="meterDevice.lastMeterDate">
              {{ meterDevice.lastMeterDate |  formatDate($t('format_date'))}}
            </span>
            <span v-else>-</span>
          </v-col>
          <v-col class="label"  sm="3">
            {{ $t('electricity-tariff.value-per-kwh') }} :
          </v-col>
          <v-col sm="3">
            <meter-device-energy-price-module
                :meter-device-id="meterDevice.id"
                readonly
            />
          </v-col>
        </v-row>
      </template>

      <template v-if="isExternalOperator">
        <headline class="mt-10">
            {{ $t("enums.meter-device.external-operator") }}
        </headline>
        <v-row>
          <v-col class="label"  sm="3">
            {{ $t('meter-device.external-operator.billing-cycle') }} :
          </v-col>
          <v-col sm="3">
            {{ $t('enums.billing-cycle.' + meterDevice.externalOperatorBillingCycle) }}
          </v-col>

          <v-col class="label"  sm="3">
            {{ $tc('meter-device.external-operator.supplier') }} :
          </v-col>
          <v-col sm="3">
            {{ meterDevice.externalOperatorSupplierName }}
          </v-col>
        </v-row>
      </template>



    </div>

  </div>
</template>

<script>

import MeterDeviceEnergyPriceModule from "@pages/MeterDevice/Detail/modals/MeterDeviceEnergyPriceModule.vue";
import VehicleRepository from "@repository/VehicleRepository";

export default {
  components: {MeterDeviceEnergyPriceModule},
  data() {
    return {
      vehicleLicenceOk: null, // null = unknown, true/false = Connected Vehicle Meter licence active?
    }
  },
  computed: {
    meterDevice() {
      return this.$parent.meterDevice ?? null
    },

    isExternalOperator() {
      return this.meterDevice.type === 'external-operator'
    },

    isConnectedVehicle() {
      return this.meterDevice?.type === 'connected-vehicle'
    },

  },

  watch: {
    meterDevice: {
      immediate: true,
      handler(md) {
        if (md && md.type === 'connected-vehicle' && md.vehicle && md.vehicle.id) {
          VehicleRepository.connectedVehicleMeterLicence(md.vehicle.id).then(res => {
            this.vehicleLicenceOk = res.hasLicence === true
          }).catch(() => {
            this.vehicleLicenceOk = null
          })
        }
      }
    }
  },

  methods: {
    customerName(customer) {
      if(customer) {

        if(customer.name) {
          return customer.name
        }
        let names = []
        if(customer.firstname) {
          names.push(customer.firstname)
        }

        if(customer.lastname) {
          names.push(customer.lastname)
        }
        if(names.length > 0) {
          return names.join(' ')
        }

        if(customer.evcoId) {
          return customer.evcoId
        }
      }
      return 'no name'
    },

  }
}
</script>

<style lang="scss">


.organization-overview {

}

</style>
